import type { IconProp } from '@fortawesome/fontawesome-svg-core'
import type { Dispatch, KeyboardEvent, SetStateAction } from 'react'
import type { MenuGroup, MenuLink } from './menu'
import { faAngleRight, faMagnifyingGlass, faXmark } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import md5 from 'md5'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useUser } from '../../../providers/UserProvider'
import { checkPermission } from '../../../util/checkPermission'
import { getScreenSize } from '../../../util/helpers'
import logo from './logo512.png'
import { findActiveLink, isGroup, MENU, searchMenu, visibleMenu } from './menu'

import './MenuSidebar.css'

const DEFAULT_TITLE = 'Datafiber Network Management'

interface MenuSidebarProps {
  sidebar: {
    get: boolean
    set: Dispatch<SetStateAction<boolean>>
  }
}

function SidebarLink({ link, active }: { link: MenuLink, active: boolean }) {
  return (
    <li className="nav-item">
      <Link to={link.path} className={`nav-link${active ? ' active' : ''}`} aria-current={active ? 'page' : undefined}>
        <FontAwesomeIcon icon={link.icon as IconProp} className="nav-icon" />
        <p>{link.label}</p>
      </Link>
    </li>
  )
}

interface SidebarGroupProps {
  group: MenuGroup
  open: boolean
  activePath?: string
  onToggle: () => void
}

function SidebarGroup({ group, open, activePath, onToggle }: SidebarGroupProps) {
  const containsActive = group.links.some(link => link.path === activePath)

  return (
    <li className={`nav-item${open ? ' menu-open' : ''}`}>
      <a
        href="#"
        role="button"
        aria-expanded={open}
        className={`nav-link${containsActive ? ' active' : ''}`}
        onClick={(event) => {
          event.preventDefault()
          onToggle()
        }}
        onKeyDown={(event) => {
          if (event.key === ' ') {
            event.preventDefault()
            onToggle()
          }
        }}
      >
        <FontAwesomeIcon icon={group.icon as IconProp} className="nav-icon" />
        <p>
          {group.label}
          {/* AdminLTE 4 rotates .nav-arrow a quarter turn while the item is open */}
          <FontAwesomeIcon icon={faAngleRight as IconProp} className="nav-arrow" />
        </p>
      </a>
      <ul className="nav nav-treeview">
        {group.links.map(link => <SidebarLink key={link.path} link={link} active={link.path === activePath} />)}
      </ul>
    </li>
  )
}

function MenuSidebar(props: MenuSidebarProps) {
  const { user, permissions } = useUser()
  const location = useLocation()
  const navigate = useNavigate()

  const menu = useMemo(() => visibleMenu(MENU, node => checkPermission(node, 'read', permissions)), [permissions])
  const active = findActiveLink(menu, location.pathname)
  const activeGroup = active?.group?.label
  const activeTitle = active?.link.label

  // Accordion: one group open at a time, and after every navigation the one holding the current page.
  const [openGroup, setOpenGroup] = useState(activeGroup)
  useEffect(() => {
    setOpenGroup(activeGroup)
  }, [activeGroup, location.pathname])

  useEffect(() => {
    document.title = activeTitle ? `${activeTitle} · Datafiber Network` : DEFAULT_TITLE
  }, [activeTitle])

  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState(0)
  const results = useMemo(() => searchMenu(menu, query), [menu, query])
  const searchRef = useRef<HTMLInputElement>(null)

  const clearSearch = () => {
    setQuery('')
    setSelected(0)
  }

  // Ctrl+K (Cmd+K) shows the sidebar and puts the cursor in the search field.
  const setSidebar = props.sidebar.set
  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'k')
        return
      event.preventDefault()
      // Main's flag means "icon-only rail" from lg up but "off-canvas open" below lg.
      setSidebar(!['lg', 'xl'].includes(getScreenSize()))
      requestAnimationFrame(() => searchRef.current?.focus())
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [setSidebar])

  const onSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      const step = event.key === 'ArrowDown' ? 1 : -1
      setSelected(index => Math.max(0, Math.min(index + step, results.length - 1)))
    }
    else if (event.key === 'Enter' && results[selected]) {
      navigate(results[selected].link.path)
      clearSearch()
      event.currentTarget.blur()
    }
    else if (event.key === 'Escape') {
      clearSearch()
      event.currentTarget.blur()
    }
  }

  // Red accent on development, blue in production, so the two are told apart at a glance.
  const sidebarAccent = import.meta.env.DEV ? 'sidebar-accent-red' : 'sidebar-accent-blue'

  return (
    <aside className={`app-sidebar bg-body-secondary shadow-lg ${sidebarAccent}`} data-bs-theme="dark">
      <div className="sidebar-brand">
        <Link to="/" className="brand-link">
          <img
            src={logo}
            alt="Datafiber.Network logo"
            className="brand-image rounded-circle shadow opacity-75"
          />
          <span className="brand-text fw-light">Datafiber.Network</span>
        </Link>
      </div>
      <div className="sidebar-wrapper">
        <div className="user-panel mt-2 pb-3 mb-3 d-flex">
          <div className="image">
            <img
              src={`https://www.gravatar.com/avatar/${md5(user.email ? user.email : 'operations@datafiber.nl')}?s=160&d=retro&r=pg`}
              className="rounded-circle shadow-sm"
              alt="User"
            />
          </div>
          <div className="info">
            {user.firstName}
            {' '}
            {user.lastName}
          </div>
        </div>
        <div className="sidebar-search">
          <FontAwesomeIcon icon={faMagnifyingGlass as IconProp} className="sidebar-search-icon" />
          <input
            ref={searchRef}
            className="form-control"
            type="search"
            placeholder="Zoek een pagina"
            aria-label="Menu doorzoeken"
            aria-keyshortcuts="Control+K"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value)
              setSelected(0)
            }}
            onKeyDown={onSearchKeyDown}
            // Pages with a keyboard barcode scanner listen to window keypresses; typing here is not a scan.
            onKeyPress={event => event.stopPropagation()}
          />
          {query
            ? (
                <button
                  type="button"
                  className="sidebar-search-clear"
                  aria-label="Zoekopdracht wissen"
                  onClick={() => {
                    clearSearch()
                    searchRef.current?.focus()
                  }}
                >
                  <FontAwesomeIcon icon={faXmark as IconProp} />
                </button>
              )
            : <kbd className="sidebar-search-hint" aria-hidden="true">Ctrl K</kbd>}
        </div>
        <nav>
          <ul className="nav sidebar-menu flex-column nav-indent">
            {query
              ? (results.length
                  ? results.map(({ link, group }, index) => (
                      <li key={link.path} className="nav-item">
                        <Link to={link.path} className={`nav-link${index === selected ? ' search-selected' : ''}`} onClick={clearSearch}>
                          <FontAwesomeIcon icon={link.icon as IconProp} className="nav-icon" />
                          <p>
                            {link.label}
                            {group && <small className="ms-2 opacity-50">{group.label}</small>}
                          </p>
                        </Link>
                      </li>
                    ))
                  : <li className="nav-header">Geen resultaten</li>)
              : menu.map((section, index) => (
                  <React.Fragment key={section.label ?? index}>
                    {section.label && <li className="nav-header text-uppercase">{section.label}</li>}
                    {section.items.map(item => isGroup(item)
                      ? (
                          <SidebarGroup
                            key={item.label}
                            group={item}
                            open={openGroup === item.label}
                            activePath={active?.link.path}
                            onToggle={() => setOpenGroup(open => open === item.label ? undefined : item.label)}
                          />
                        )
                      : <SidebarLink key={item.path} link={item} active={item.path === active?.link.path} />)}
                  </React.Fragment>
                ))}
          </ul>
        </nav>
      </div>
    </aside>
  )
}

export default MenuSidebar
